"use client";

import { useToast } from "@/app/hooks/toast/useToast";
import type { contextProps } from "@/app/types/globalState";
import type { APIUserData, UserDetails } from "@/app/types/user";
import {
  getAuthChallenge,
  verifySignedMessage,
  getUserData,
  removeUserData,
} from "@/app/utils/apis/apiService";
import type { WalletProviderType } from "@/app/utils/services/enum/wallet-provider-type";
import { getWalletProviderType } from "@/app/utils/services/service/localStorageService";
import {
  clearWalletData,
  storeWalletData,
} from "@/app/utils/wallet/walletUtils";
import { usePathname } from "@/i18n/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { signLoginPayload } from "thirdweb/auth";
import { useActiveWallet, useDisconnect } from "thirdweb/react";

type WalletServiceContextProps = {
  userDetails: UserDetails;
  getConnectedWallet: () => string | undefined;
  loader: boolean;
  onDisconnect: () => void;
};

const WalletServiceContext = createContext<
  WalletServiceContextProps | undefined
>(undefined);
export const useWalletService = () => {
  const context = useContext(WalletServiceContext);
  if (!context) {
    throw new Error(
      "useWalletService must be used within a WalletServiceProvider"
    );
  }
  return context;
};

type Props = {
  user: APIUserData | null;
  children: ReactNode;
};

const WalletServiceProvider = ({ children, user }: Props) => {
  const walletService = useActiveWallet();
  const [loader, setLoader] = useState<boolean>(false);
  const [walletServiceInitialized, setWalletServiceInitialized] = useState(
    walletService ? true : false
  );
  const { showToast } = useToast();
  const { disconnect } = useDisconnect();

  const [userDetails, setUserDetails] = useState<UserDetails>({
    loggedIn: !!user,
    walletProviderType: "",
    walletId: user?.walletId ?? "",
    points: user?.points ?? 0,
    refUrl: user?.refUrl ?? "",
    email: user?.email ?? "",
    isCBOneAttested: false,
  });

  const [, pathname] = usePathname().split("/");

  const cleanup = useCallback(async () => {
    clearWalletData();
    await removeUserData();
  }, []);

  const handleDisconnect = async () => {
    if (walletService) {
      disconnect(walletService);
    }

    await cleanup();
    window.location.replace("/");
  };

  const getConnectedWallet = () => {
    return getWalletProviderType();
  };

  const handleConnect = useCallback(async () => {
    if (userDetails.loggedIn) {
      return;
    }

    await cleanup();

    if (!walletService && !walletServiceInitialized) {
      return;
    }

    if (!walletService && walletServiceInitialized) {
      setUserDetails({
        loggedIn: false,
        walletProviderType: "",
        walletId: "",
        points: 0,
        refUrl: "",
        email: "",
        isCBOneAttested: false,
      });

      if (pathname === "account") {
        window.location.replace("/");
      }

      return;
    }

    setLoader(true);

    const { data, error } = await getAuthChallenge(
      walletService?.getAccount()?.address!
    );

    if (error || !data?.payload) {
      setLoader(false);
      return;
    }

    let signature: string;
    let payload: any;

    try {
      const { signature: signSignature, payload: signPayload } =
        await signLoginPayload({
          payload: data?.payload,
          account: walletService?.getAccount()!,
        });
      signature = signSignature;
      payload = signPayload;
    } catch (signError: any) {
      if (signError?.message.includes("Pop up")) {
        showToast(
          "Signature verification failed. Your browser may have blocked the wallet popup — please allow it.",
          "error"
        );
      } else {
        showToast("Signature verification failed. Please try again.", "error");
      }
      if (walletService) {
        disconnect(walletService);
      }

      setLoader(false);
      return;
    }

    const { data: tokenData } = await verifySignedMessage(
      walletService?.getAccount()?.address!,
      signature,
      payload
    );

    console.log(tokenData);

    if (tokenData?.success) {
      const userWalletData = await getUserData();

      storeWalletData(
        walletService?.id as WalletProviderType,
        walletService?.getAccount()?.address?.toLowerCase() ?? ""
      );

      setUserDetails({
        loggedIn: true,
        walletProviderType: walletService?.id ?? "",
        walletId: walletService?.getAccount()?.address?.toLowerCase() ?? "",
        points: userWalletData?.points ?? 0,
        refUrl: userWalletData?.refUrl ?? "",
        email: userWalletData?.email ?? "",
        isCBOneAttested: userWalletData?.isCBOneAttested || false,
      });

      setLoader(false);
    }

    setWalletServiceInitialized(true);
    setLoader(false);
  }, [
    cleanup,
    disconnect,
    pathname,
    userDetails.loggedIn,
    walletService,
    walletServiceInitialized,
  ]);

  useEffect(() => {
    handleConnect();
  }, [handleConnect]);

  return (
    <WalletServiceContext.Provider
      value={{
        userDetails,
        loader,
        getConnectedWallet,
        onDisconnect: handleDisconnect,
      }}
    >
      {children}
    </WalletServiceContext.Provider>
  );
};

export default WalletServiceProvider;
